/**
 * ==============================================
 * ADD TO CART HANDLER - RECHARGE INTEGRATION
 * ==============================================
 *
 * This module handles the cart operations for the subscription picker.
 * It listens for custom events from MonthsSubscriptionPicker and:
 * 1. Processes selected scents through Recharge bundle system
 * 2. Adds shower pack (if applicable) with correct subscription plan
 * 3. Adds any selected add-ons
 * 4. Adds all items to Shopify cart
 * 5. Redirects to checkout
 *
 * RECHARGE INTEGRATION:
 * - Uses Recharge CDN bundle settings for scent selection
 * - Applies correct selling plans for subscriptions
 * - Bundles multiple scents into single cart item with Recharge metadata
 *
 * CART ITEM STRUCTURE:
 * - Each item has variant ID, quantity, and optional selling_plan
 * - All items get unique _bundle_id property (timestamp) for grouping
 * - Add-ons are separate cart items without subscription plans
 *
 * DEPENDENCIES:
 * - recharge.cdn global object (loaded from Recharge CDN)
 * - theme.routes global object (Shopify routes)
 * - MonthsSubscriptionPicker component (dispatches events)
 */
(async function () {
  /**
   * Check if customer already has shower pack
   * Affects which variants and pricing to use
   */
  const refillProductOnly = document.querySelector('[data-refill-product-only]')?.dataset.refillProductOnly === 'true';

  /**
   * Load product configuration from JSON script tag
   * Contains variant IDs, bundle settings, images, etc.
   */
  const productsData = JSON.parse(document.getElementById('months-product-subscription-picker-data').textContent);

  /**
   * Per-plan bundle config is read at add-to-cart time from
   * productsData.plan_cards_config[<frequency>]. Recharge bundle source-collection
   * IDs are configured on the variant picker block (productsData.scent_source_collection_id
   * and productsData.shower_pack_source_collection_id) and must match the source
   * collections configured on the bundle product in Recharge.
   */

  /**
   * Shower pack variants (applicator + wall mount + refill)
   *
   * VARIANT STRUCTURE:
   * The shower pack product has multiple variants organized by:
   * 1. Subscription type (determines selling plan attachment)
   * 2. Color (e.g., Black, Cream - filtered later based on selection)
   *
   * INDEX MAPPING (when filtered by color):
   * [0] = One-time purchase variant (no selling_plan_allocations)
   * [1] = Monthly subscription variant (selling_plan_allocations[0] = monthly plan)
   * [2] = Quarterly subscription variant (selling_plan_allocations[1] = quarterly plan)
   *
   * Note: "varaints" spelling is intentional (legacy naming in data structure)
   *
   * FALLBACK CHAIN:
   * 1. window.shower_shower_pack_product_varaints (allows external override)
   * 2. productsData.shower_shower_pack_product_varaints (from JSON config)
   * 3. Empty array (safe fallback)
   */
  const shower_shower_pack_product_varaints = window.shower_shower_pack_product_varaints || productsData.shower_shower_pack_product_varaints || [];

  /**
   * Discovery Plan shower pack product (e.g. "the-discovery-plan")
   * Used instead of regular shower pack when quarterly is selected.
   * Falls back to regular shower pack if not configured.
   */
  const discovery_shower_pack_product_varaints = productsData.discovery_shower_pack_product_varaints || shower_shower_pack_product_varaints;

  /**
   * ==============================================
   * EVENT LISTENER: Add to Cart Requested
   * ==============================================
   * Triggered when MonthsSubscriptionPicker's handleAddToCart() dispatches event
   *
   * EVENT PAYLOAD (e.detail):
   * - refillFrequency: 'monthly' | 'quarterly' | 'trial' | 'oneoff'
   * - selectedScents: Array of scent objects with variantId and quantity
   * - selectedAddons: Array of add-on products
   * - productData: Pricing option from prices_map
   * - pricing: Price breakdown object
   */
  document.body.addEventListener('subscription-picker:addToCartRequested', async (e) => {
    const payloadData = e.detail;
    //console.log('payloadData', payloadData);
    const picker = e.target;
    const addToCartBtn = picker.querySelector('[data-add-to-cart-btn]');

    // Disable button during async operation
    updateButtonState(addToCartBtn, true);

    /**
     * ==============================================
     * STEP 1: SELECT SHOWER PACK VARIANT
     * ==============================================
     * Choose correct variant based on refill frequency and selected color.
     *
     * QUARTERLY (Discovery Plan):
     *   Uses "the-discovery-plan" product — one variant per color, index 0.
     *   Selling plans: [0] = monthly, [1] = quarterly.
     *
     * MONTHLY / ONE-TIME / GIFT:
     *   Uses regular shower pack — variants per subscription type × color.
     *   After color filter: [0] = one-time, [1] = monthly, [3] = gift.
     */
    const isQuarterly = payloadData.refillFrequency === 'quarterly';
    const isTrial = payloadData.refillFrequency === 'trial';
    const isGift = payloadData.refillFrequency === 'gift';
    const isOneoff = payloadData.refillFrequency === 'oneoff';
    const includeShowerPack = !refillProductOnly || isGift;
    const hasDiscoveryProduct = discovery_shower_pack_product_varaints && discovery_shower_pack_product_varaints.length > 0;

    let activeShowerPackVariants, activeColorOption;
    if (isQuarterly && hasDiscoveryProduct) {
      activeShowerPackVariants = discovery_shower_pack_product_varaints;
      activeColorOption = productsData.discovery_color_option || productsData.color_option;
    } else {
      // Monthly, one-time, gift, trial, and quarterly (if no discovery product) all use the regular shower pack
      activeShowerPackVariants = shower_shower_pack_product_varaints;
      activeColorOption = productsData.color_option;
    }

    // Filter variants by selected color if color is selected
    let filteredVariants = activeShowerPackVariants;
    if (payloadData.selectedColor && activeColorOption) {
      const colorOptionPosition = activeColorOption.position;
      const optionKey = `option${colorOptionPosition}`;

      filteredVariants = activeShowerPackVariants.filter((variant) => {
        return variant[optionKey] === payloadData.selectedColor;
      });

      //console.log('Filtered variants by color:', payloadData.selectedColor, JSON.stringify(filteredVariants));
    }

    // Look up the plan config by the selected plan's unique id first. Two cards
    // can share a frequency (e.g. a 6-week and a 3-month plan both "quarterly"),
    // so keying by frequency alone would pick the wrong bundle/variant. Fall back
    // to the frequency key for legacy (non plan-card) products.
    const planCardCfg =
      (payloadData.planId && productsData.plan_cards_config?.[payloadData.planId]) ||
      productsData.plan_cards_config?.[payloadData.refillFrequency] ||
      {};
    const planCardOverrideIndex = planCardCfg.shower_pack_variant_index;
    const hasPlanCardOverride = planCardOverrideIndex !== '' && planCardOverrideIndex != null && !isNaN(parseInt(planCardOverrideIndex, 10));

    let showerPackVariantIndex;
    if (hasPlanCardOverride) {
      showerPackVariantIndex = parseInt(planCardOverrideIndex, 10);
    } else if (isTrial) {
      // Trial applicator: one-time variant at index 4 in regular shower pack
      showerPackVariantIndex = 4;
    } else if (payloadData.refillFrequency === 'gift' && filteredVariants?.length > 3) {
      // add 20% discount for gift purchases
      showerPackVariantIndex = 3;
    } else {
      // Monthly, quarterly, and one-time all use index 0
      showerPackVariantIndex = 0;
    }

    const shower_shower_pack_varaint = filteredVariants?.[showerPackVariantIndex];
    const showerPackVariantId = shower_shower_pack_varaint?.id;

    /**
     * Get selling plan ID for shower pack (subscriptions only)
     * - Monthly: First selling plan allocation
     * - Quarterly: Second selling plan allocation (same convention for both products)
     * - One-time: Empty string (no subscription)
     */
    let showerPackSellingPlanId = '';
    if (payloadData.refillFrequency === 'monthly') {
      showerPackSellingPlanId = shower_shower_pack_varaint?.selling_plan_allocations?.[0]?.selling_plan_id || '';
    } else if (payloadData.refillFrequency === 'quarterly') {
      showerPackSellingPlanId = shower_shower_pack_varaint?.selling_plan_allocations?.[1]?.selling_plan_id || '';
    }
    // Trial: no selling plan on applicator — it's added as one-time.
    // The subscription is managed by the dynamic bundle (14-days-trial-bundle).

    /**
     * ==============================================
     * STEP 2: BUILD RECHARGE BUNDLE FOR SCENTS
     * ==============================================
     * Recharge bundles allow customers to select multiple scent variants
     * that are packaged as a single "bundle" product in the cart.
     *
     * BUNDLE STRUCTURE:
     * - externalProductId: Bundle product ID
     * - externalVariantId: Bundle variant ID
     * - selections: Array of selected scent variants with quantities
     *
     * For subscriptions, each selection includes a sellingPlan ID.
     * For one-time purchases, sellingPlan is removed.
     */
    // Resolve bundle configuration for the active plan
    const activeBundleProductId = planCardCfg.bundle_product_id;
    const activeBundleVariant = planCardCfg.bundle_product_variants?.[0];
    const activeBundleVariantId = activeBundleVariant?.id;
    const useFixedPrice = !!planCardCfg.use_fixed_price_bundle;

    // A gift card using the standard (non-bespoke) UI sells items individually
    // as one-time purchases. The regular one-time plan uses its configured
    // Recharge fixed-price bundle when one is set, otherwise it also sells
    // items individually (e.g. the refill-only one-time plan).
    const isGiftIndividual = isGift && planCardCfg.show_gift_card_recipient === false;
    const isOneoffIndividual = isOneoff && !activeBundleProductId;

    let bundleItems = [];
    let buildBundleItemsForShowerPackFn = null;
    if (payloadData.selectedScents && payloadData.selectedScents.length > 0) {
      if (isGiftIndividual || isOneoffIndividual) {
        // Standard gift or bundle-less one-time purchase: sell scents as
        // standalone one-time products, without a Recharge bundle or subscription.
        bundleItems = payloadData.selectedScents.map((scent) => ({
          id: scent.variantId,
          quantity: scent.quantity,
        }));
        if (includeShowerPack && showerPackVariantId) {
          bundleItems.push({ id: showerPackVariantId, quantity: 1 });
        }
      } else if (!activeBundleProductId || !activeBundleVariantId) {
        console.error(`Plan "${payloadData.refillFrequency}" has no bundle_product configured`);
        alert('This plan is misconfigured. Please contact support.');
        updateButtonState(addToCartBtn, false);
        return;
      } else if (isTrial) {
        // Trial flow: bundle product variants represent scents (e.g. "The Modern Man");
        // each becomes a separate one-time line item carrying its selling plan.
        const trialVariants = planCardCfg.bundle_product_variants || [];
        bundleItems = payloadData.selectedScents
          .map((s) => {
            const scentTitle = s.title.replace(/^Scent:\s*/i, '').trim();
            const matchedVariant = trialVariants.find((v) => v.title.toLowerCase().includes(scentTitle.toLowerCase()));
            if (!matchedVariant) {
              console.warn('No matching trial variant found for scent:', s.title);
              return null;
            }
            return {
              id: matchedVariant.id,
              quantity: s.quantity,
              selling_plan: matchedVariant.selling_plan_allocations?.[0]?.selling_plan_id || '',
            };
          })
          .filter(Boolean);
      } else {
        const scentSourceCollectionId = productsData.scent_source_collection_id;
        const showerPackSourceCollectionId = productsData.shower_pack_source_collection_id;
        // Refill-only templates do not add a shower pack, so they only need the
        // scent source collection configured on the Recharge bundle.
        if (!scentSourceCollectionId || (includeShowerPack && !showerPackSourceCollectionId)) {
          console.error('Bundle source collections are not configured on the variant picker block.');
          alert('This product is misconfigured. Please contact support.');
          updateButtonState(addToCartBtn, false);
          return;
        }

        // Builds the cart items for a single buyer/recipient. The shower pack is
        // included as a bundle selection from its own source collection.
        // Gift recipients each get their own bundle (and their own _rb_id for fixed-price)
        // because shower pack colour differs per recipient.
        async function buildBundleItemsForShowerPack(showerPackVariant) {
          const selections = payloadData.selectedScents.map((scentSelection) => ({
            collectionId: scentSourceCollectionId,
            externalProductId: scentSelection.productId,
            externalVariantId: scentSelection.variantId,
            quantity: scentSelection.quantity,
            sellingPlan: scentSelection.sellingPlanId,
          }));

          if (includeShowerPack && showerPackVariant) {
            let showerPackSellingPlan;
            if (payloadData.refillFrequency === 'monthly') {
              showerPackSellingPlan = showerPackVariant.selling_plan_allocations?.[0]?.selling_plan_id;
            } else if (isQuarterly) {
              showerPackSellingPlan = showerPackVariant.selling_plan_allocations?.[1]?.selling_plan_id;
            }
            const showerPackProductId =
              isQuarterly && hasDiscoveryProduct
                ? productsData.discovery_shower_pack_product_id
                : productsData.shower_shower_pack_product_id;
            selections.push({
              collectionId: showerPackSourceCollectionId,
              externalProductId: showerPackProductId,
              externalVariantId: showerPackVariant.id,
              quantity: 1,
              sellingPlan: showerPackSellingPlan,
            });
          }

          // Remove selling plans for one-time purchases and gifts
          if (isOneoff || isGift) {
            selections.forEach((selection) => delete selection.sellingPlan);
          }

          const bundle = {
            externalProductId: activeBundleProductId,
            externalVariantId: activeBundleVariantId,
            selections,
          };

          if (useFixedPrice) {
            // Fixed-price bundle: single line item priced at the bundle variant's price.
            // Recharge resolves the customer's selections from Redis via _rb_id at checkout.
            const sellingPlanIndex = parseInt(planCardCfg.selling_plan_index, 10);
            const bundleSellingPlanId =
              isOneoff || isGift
                ? ''
                : activeBundleVariant?.selling_plan_allocations?.[isNaN(sellingPlanIndex) ? 0 : sellingPlanIndex]?.selling_plan_id || '';

            const rbId = await recharge.bundle.getBundleId(bundle);

            // Assemble a human-readable contents string so the cart drawer's
            // "What's Included" renderer can display selections without round-tripping Recharge.
            const bundleContents = payloadData.selectedScents.map((s) => {
              const title = (s.title || '').replace(/^Scent:\s*/i, '').trim();
              return {
                product_id: s.productId,
                variant_id: s.variantId,
                quantity: s.quantity,
                title,
              };
            });
            const contentsLines = bundleContents.map((s) => {
              const title = s.title || 'Bundle product';
              return `${s.quantity} x ${title}`;
            });
            if (includeShowerPack && showerPackVariant) {
              contentsLines.push(`1 x ${showerPackVariant.title || 'Shower Pack'}`);
              bundleContents.push({
                product_id: isQuarterly && hasDiscoveryProduct ? productsData.discovery_shower_pack_product_id : productsData.shower_shower_pack_product_id,
                variant_id: showerPackVariant.id,
                quantity: 1,
                title: showerPackVariant.title || 'Shower Pack',
              });
            }
            const contentsPropertyName = planCardCfg.bundle_contents_property || '_bundle_contents';

            const fixedPriceItem = {
              id: activeBundleVariantId,
              quantity: 1,
              properties: {
                _rb_id: rbId,
                [contentsPropertyName]: contentsLines.join('\n'),
                _bundle_contents_data: JSON.stringify(bundleContents),
              },
            };
            if (bundleSellingPlanId) fixedPriceItem.selling_plan = bundleSellingPlanId;
            return [fixedPriceItem];
          }

          // Dynamic bundle: Recharge expands selections into multiple Shopify line items
          return recharge.bundle.getDynamicBundleItems(bundle, 'shopifyProductHandle');
        }

        // Expose helper for STEP 4 (gift recipients regenerate per-recipient)
        buildBundleItemsForShowerPackFn = buildBundleItemsForShowerPack;

        // Non-gift: build once with the resolved shower pack variant.
        // Gift: skip — STEP 4 builds per recipient.
        if (!isGift) {
          bundleItems = await buildBundleItemsForShowerPack(shower_shower_pack_varaint);
        }
      }
    } else {
      console.warn('No scents selected for cart addition');
    }

    /**
     * ==============================================
     * STEP 3: BUILD COMPLETE ITEMS ARRAY
     * ==============================================
     * Combine all cart items:
     * 1. Bundle items (scents from Recharge)
     * 2. Shower pack (if not refill-only customer)
     * 3. Add-ons (extra products for one-time purchases)
     */
    const items = [...bundleItems];

    /**
     * Shower pack handling:
     * - Trial: still added as a separate one-time line item (trial flow keeps
     *   the applicator outside the bundle).
     * - All other flows: shower pack is included inside the Recharge bundle's
     *   selections (see buildBundleItemsForShowerPack above), so nothing to push here.
     */
    if (isTrial && includeShowerPack && showerPackVariantId) {
      const showerPackItem = { id: showerPackVariantId, quantity: 1 };
      if (!isOneoff && showerPackSellingPlanId) {
        showerPackItem.selling_plan = showerPackSellingPlanId;
      }
      items.push(showerPackItem);
    }

    /**
     * Add selected add-ons (one-time purchases only)
     * Add-ons are additional products without subscription plans
     */
    if (payloadData.selectedAddons && payloadData.selectedAddons.length > 0) {
      for (const addon of payloadData.selectedAddons) {
        if (addon.bundle) {
          // Fixed-price upsell bundles use product_1 as the parent and the
          // remaining configured products as Recharge selections.
          const rbId = await recharge.bundle.getBundleId(addon.bundle);
          items.push({
            id: addon.bundle.externalVariantId,
            quantity: 1,
            properties: {
              ...addon.properties,
              _rb_id: rbId,
            },
          });
          continue;
        }

        const addonItem = {
          id: addon.id,
          quantity: addon.quantity,
        };
        if (addon.properties) addonItem.properties = addon.properties;
        items.push(addonItem);
      }
    }

    // Safety check: Ensure we have items to add.
    // Gift flow builds items per-recipient in STEP 4, so an empty `items` here is expected.
    const hasGiftRecipients = isGift && payloadData.giftNotification?.recipients?.length > 0;
    if (items.length === 0 && !hasGiftRecipients) {
      alert(`Something went wrong. Please try again.`);
      return;
    }

    /**
     * ==============================================
     * STEP 4: ADD BUNDLE TRACKING & CREATE SEPARATE LINE ITEMS PER RECIPIENT
     * ==============================================
     * For gift purchases with multiple recipients:
     * - Create separate line items for each recipient
     * - Each line item gets unique properties for that specific recipient
     * - This allows easy tracking and fulfillment in Shopify
     *
     * For non-gift or single recipient:
     * - Add standard bundle tracking properties
     */
    const bundleId = new Date().getTime();
    let itemsWithProperties = [];

    if (isGift && payloadData.giftNotification?.recipients) {
      const recipients = payloadData.giftNotification.recipients;

      const recipientPropsFor = (recipient, index) => ({
        _bundle_id: bundleId,
        _is_gift: 'Yes',
        _recipient_count: recipients.length.toString(),
        _recipient_number: (index + 1).toString(),
        _Recipient_colour: recipient.recipientColor,
        _Recipient_email: recipient.recipientEmail,
        _Recipient_first_name: recipient.recipientFirstName,
        _Recipient_occasion: recipient.occasion,
        _Recipient_klaviyo_list_id: recipient.klaviyoListId,
        _Recipient_klaviyo_list_name: recipient.klaviyoListName,
        ...(recipient.day && recipient.month && { _Recipient_date: `${recipient.day}/${recipient.month}` }),
      });

      for (let index = 0; index < recipients.length; index++) {
        const recipient = recipients[index];

        // Resolve this recipient's shower pack variant by colour
        let recipientShowerPackVariant = shower_shower_pack_varaint;
        if (recipient.recipientColor && productsData.color_option) {
          const colorOptionPosition = productsData.color_option.position;
          const optionKey = `option${colorOptionPosition}`;
          const recipientFilteredVariants = shower_shower_pack_product_varaints.filter(
            (variant) => variant[optionKey] === recipient.recipientColor
          );
          recipientShowerPackVariant = recipientFilteredVariants?.[showerPackVariantIndex];
        }
        const recipientShowerPackVariantId = recipientShowerPackVariant?.id;

        let recipientItems;
        if (isTrial) {
          // Trial: shower pack is a separate line item — swap its variant id per recipient.
          recipientItems = items.map((item) => ({
            ...item,
            id: item.id === showerPackVariantId ? recipientShowerPackVariantId : item.id,
          }));
        } else {
          // Non-trial: regenerate the bundle with this recipient's shower pack variant
          // (new _rb_id for fixed-price; new dynamic items for dynamic).
          const recipientBundleItems = buildBundleItemsForShowerPackFn
            ? await buildBundleItemsForShowerPackFn(recipientShowerPackVariant)
            : [];
          const recipientAddons = [];
          for (const addon of payloadData.selectedAddons || []) {
            if (addon.bundle) {
              const rbId = await recharge.bundle.getBundleId(addon.bundle);
              recipientAddons.push({
                id: addon.bundle.externalVariantId,
                quantity: 1,
                properties: {
                  ...addon.properties,
                  _rb_id: rbId,
                },
              });
            } else {
              recipientAddons.push({
                id: addon.id,
                quantity: addon.quantity,
                ...(addon.properties ? { properties: addon.properties } : {}),
              });
            }
          }
          recipientItems = [...recipientBundleItems, ...recipientAddons];
        }

        const props = recipientPropsFor(recipient, index);
        itemsWithProperties.push(
          ...recipientItems.map((item) => ({
            ...item,
            properties: { ...item.properties, ...props },
          }))
        );
      }
    } else {
      // Non-gift or no recipients: standard bundle properties. Gift cards using
      // the standard (non-bespoke) UI still mark items as a gift so downstream
      // automation (e.g. Shopify Flow) can tag the order.
      itemsWithProperties = items.map((item) => ({
        ...item,
        properties: {
          ...item.properties,
          _bundle_id: bundleId,
          ...(isGift && { _is_gift: 'Yes' }),
        },
      }));
    }

    //console.log('itemsWithProperties', itemsWithProperties);

    /**
     * ==============================================
     * STEP 5: ADD ITEMS TO SHOPIFY CART
     * ==============================================
     * Use Shopify AJAX Cart API to add all items at once.
     * Includes cart attributes for tracking customer type.
     */
    // Optional: Clear cart first (currently commented out)
    //await fetch(`${theme.routes.cart_clear_url}.js`);

    const response = await fetch(`${theme.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: itemsWithProperties,
        attributes: {
          'data-refill-product-only': refillProductOnly.toString(), // Track customer type
        },
      }),
    });
    const data = await response.json();
    //console.log('data', data);

    /**
     * ==============================================
     * STEP 5.4: META ADD TO CART
     * ==============================================
     * No manual fbq() call here. Shopify publishes product_added_to_cart for this
     * /cart/add.js request, and the Facebook & Instagram app sends it as AddToCart
     * via both pixel and Conversions API with a shared event_id. A manual pixel
     * call would add a browser-only duplicate with no event_id, which lowers
     * Meta's Conversions API event coverage.
     */

    /**
     * ==============================================
     * STEP 5.5: SEND GIFT RECIPIENTS TO KLAVIYO
     * ==============================================
     * If this is a gift purchase with recipients,
     * send each recipient's data to Klaviyo for 30-day follow-up.
     * This happens after cart is successfully created.
     * The widget's sendToKlaviyo() method handles all recipients.
     */
    /*     if (payloadData.refillFrequency === 'gift' && payloadData.giftNotification?.recipients?.length > 0) {
      const recipientCount = payloadData.giftNotification.recipients.length;
      //console.log(`Sending ${recipientCount} gift recipient(s) to Klaviyo...`);

      // Get reference to gift notification widget
      const giftWidget = document.querySelector('gift-notification-widget');
      if (giftWidget && typeof giftWidget.sendToKlaviyo === 'function') {
        try {
          //await giftWidget.sendToKlaviyo(); // Handles all recipients internally
          //console.log(`Successfully sent ${recipientCount} recipient(s) to Klaviyo`);
        } catch (error) {
          // Log error but don't block checkout
          console.error('Failed to send gift recipients to Klaviyo:', error);
        }
      } else {
        console.warn('Gift notification widget not found or sendToKlaviyo method not available');
      }
    } */

    // Re-enable button after operation
    updateButtonState(addToCartBtn, false);

    /**
     * ==============================================
     * STEP 6: REDIRECT TO CHECKOUT
     * ==============================================
     * Programmatically click hidden form's checkout button.
     * This triggers Shopify's checkout flow with cart items.
     */
    document.querySelector('#subscription-cart-form button[name="checkout"]').click();
  });

  /**
   * Helper function to manage button loading state
   * @param {HTMLElement} btn - Button element to update
   * @param {boolean} isLoading - Whether button should show loading state
   */
  function updateButtonState(btn, isLoading) {
    if (isLoading) {
      btn.setAttribute('disabled', true);
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('aria-busy');
    }
  }
})();
